/**
 * Bulk media-op reliability (#GR9a/#GR9b). Organising media one-item-at-a-time was the
 * gate's biggest UX finding (clearing a 73-image gallery = 73 clicks). This fans a
 * per-item action over a SELECTION on top of the existing per-item endpoints (no new
 * backend): assign/move = media-assign, delete = media-delete. It reuses the upload
 * reliability shape (#GR9g) — bounded concurrency + retry — and, crucially, reconciles
 * HONESTLY: one item's failure neither halts the rest nor masquerades as success, so the
 * caller can always say "48 of 50 moved · 2 failed" instead of a silent partial.
 */
import { runPool, withRetry, type RetryOptions } from './uploadPool.ts';

// Match the uploader's concurrency — fast, but under the function/DB rate-limit.
export const BULK_CONCURRENCY = 4;

export interface BulkResult {
  total: number;
  ok: number;
  failed: string[]; // the ids whose action failed after retries — never silently dropped
}

/**
 * Run `action` for every id with bounded concurrency + per-item retry. A thrown item is
 * CONTAINED (recorded in `failed`, never halts the pool). Returns authoritative counts.
 */
export async function runBulk(
  ids: string[],
  action: (id: string) => Promise<unknown>,
  opts: { concurrency?: number; retry?: RetryOptions } = {},
): Promise<BulkResult> {
  const failed: string[] = [];
  await runPool(
    ids,
    async (id) => {
      try {
        await withRetry(() => action(id), opts.retry);
      } catch {
        failed.push(id); // authoritative — a failure can't look like success
      }
    },
    opts.concurrency ?? BULK_CONCURRENCY,
  );
  return { total: ids.length, ok: ids.length - failed.length, failed };
}

/** Honest reconciliation copy — "N of M <verb>" plus the shortfall when there is one. */
export function reconcileMessage(verb: string, r: BulkResult): { message: string; kind: 'success' | 'error' } {
  return r.failed.length === 0
    ? { message: `${r.ok} of ${r.total} ${verb}.`, kind: 'success' }
    : { message: `${r.ok} of ${r.total} ${verb} · ${r.failed.length} failed — none silently dropped.`, kind: 'error' };
}

/**
 * #GR9(e): delete a group's media (fan-out, CF teardown per item) THEN the now-empty
 * group. Deletes every image with honest reconciliation via runBulk, then ALWAYS removes
 * the group — even on a partial photo-delete, so the folder never lingers half-emptied.
 * Returns the per-photo BulkResult so the caller can surface any shortfall. No new backend.
 */
export async function deleteGroupWithMedia(
  mediaIds: string[],
  deleteMedia: (id: string) => Promise<unknown>,
  deleteGroup: () => Promise<unknown>,
  opts: { concurrency?: number; retry?: RetryOptions } = {},
): Promise<BulkResult> {
  const result = await runBulk(mediaIds, deleteMedia, opts);
  await deleteGroup(); // remove the group regardless — a lingering empty folder is worse
  return result;
}
